import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Clock, Mail, MapPin, Users, Shield, Copy, CheckCircle2, Loader2, Sun, Moon, ArrowRight, Globe, CalendarDays } from 'lucide-react';
import AuthForm from '../components/AuthForm';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");

const SchedulingPage = ({ darkMode, setDarkMode }) => {
  const slug = window.location.pathname.split('/').pop();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingState, setBookingState] = useState('idle');
  const [bookingMessage, setBookingMessage] = useState('');
  const [viewer, setViewer] = useState(null);
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', note: '' });
  const [nowMs, setNowMs] = useState(Date.now());

  const shareUrl = useMemo(() => window.location.href, []);
  const hostName = data?.host?.username || slug;
  const hostHeadline = data?.host?.headline || (hostName ? `Meet ${hostName}` : 'Book a HiCapy session');
  const hostWelcome = data?.host?.welcomeMessage || 'Pick any available time slot and we will auto-send the invite.';
  const hostTagline = data?.host?.marketingHook || 'High-signal sessions powered by HiCapy.';
  const pageBg = darkMode ? 'bg-[#0F172A]' : 'bg-white';
  const headerBorder = darkMode ? 'border-white/10' : 'border-slate-200';
  const headingText = darkMode ? 'text-white' : 'text-slate-900';
  const subText = darkMode ? 'text-slate-300' : 'text-slate-600';
  const labelText = darkMode ? 'text-white/70' : 'text-slate-500';
  const panelClass = darkMode ? 'bg-white/5 border border-white/10' : 'bg-white border border-slate-200';
  const softPanelClass = darkMode ? 'bg-white/5 border border-white/10' : 'bg-white/70 border border-slate-200';
  const busyLabelText = darkMode ? 'text-white/90' : 'text-slate-800';
  const inputThemeClass = darkMode
    ? 'bg-[#0F172A] border-white/20 text-white placeholder:text-slate-400'
    : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-500';

  const availability = data?.availability;
  const hostWorkingHours = data?.host?.workingHours || availability?.workingHours || { start: '09:00', end: '17:00', timezone: availability?.timezone || 'UTC' };
  const hostTimezone = hostWorkingHours?.timezone || availability?.timezone || 'UTC';
  const hostWorkingDays = data?.host?.workingDays ?? availability?.workingDays ?? [];
  const workingDaySet = useMemo(() => new Set(hostWorkingDays), [hostWorkingDays]);
  const viewerTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch (err) {
      return 'UTC';
    }
  }, []);
  const meetingDurationMinutes = data?.host?.meetingDuration || data?.host?.preferences?.meetingDuration || availability?.meetingDuration || 30;
  const hasAvailability = Boolean(availability?.days?.length);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetch(`${API_BASE_URL}/api/schedule/${slug}`);
        const payload = await res.json();
        if (!res.ok) {
          throw new Error(payload.message || 'Unable to load availability');
        }
        setData(payload);
        setSelectedDayIndex(payload?.availability?.days?.length ? 0 : -1);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [slug]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setViewer(null);
      return;
    }

    const fetchViewer = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const payload = await res.json();
        setViewer(payload.user);
      } catch (err) {
        console.warn('Viewer profile unavailable', err.message);
      }
    };

    fetchViewer();
  }, []);

  useEffect(() => {
    const tick = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    if (!selectedSlot) return;
    const cutoff = new Date(selectedSlot.end || selectedSlot.start).getTime();
    if (cutoff <= nowMs) {
      setSelectedSlot(null);
    }
  }, [nowMs, selectedSlot]);

  useEffect(() => {
    if (viewer) {
      setForm(prev => ({
        ...prev,
        name: prev.name || viewer.username,
        email: prev.email || viewer.email
      }));
    }
  }, [viewer]);

  const selectedDay = availability?.days?.[selectedDayIndex];
  const workStartMinutes = useMemo(() => {
    const parts = hostWorkingHours?.start?.split(':').map(Number) || [9, 0];
    return parts[0] * 60 + (parts[1] || 0);
  }, [hostWorkingHours]);
  const workEndMinutes = useMemo(() => {
    const parts = hostWorkingHours?.end?.split(':').map(Number) || [17, 0];
    return parts[0] * 60 + (parts[1] || 0);
  }, [hostWorkingHours]);
  const workTotalMinutes = workEndMinutes - workStartMinutes;

  const formatter = useMemo(() => new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: hostTimezone
  }), [hostTimezone]);

  const hostTimeFormatter24 = useMemo(() => new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: hostTimezone
  }), [hostTimezone]);

  const viewerFormatter = useMemo(() => new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  }), []);

  const formatTime = iso => formatter.format(new Date(iso));
  const formatHostRange = slot => `${formatTime(slot.start)} – ${formatTime(slot.end)}`;
  const formatViewerRange = slot => `${viewerFormatter.format(new Date(slot.start))} – ${viewerFormatter.format(new Date(slot.end))}`;
  const minutesFromMidnight = (iso) => {
    const parts = hostTimeFormatter24.formatToParts(new Date(iso));
    const hours = Number(parts.find(part => part.type === 'hour')?.value || 0);
    const minutes = Number(parts.find(part => part.type === 'minute')?.value || 0);
    return hours * 60 + minutes;
  };

  const isOwner = viewer?.username && data?.host?.username && viewer.username === data.host.username;

  const copyLink = async () => {
    try {
      // Prefer native share on mobile when available
      if (navigator.share) {
        try {
          await navigator.share({ title: hostHeadline, url: shareUrl });
          setBookingMessage('Link shared');
          setTimeout(() => setBookingMessage(''), 1500);
          return;
        } catch (err) {
          // fall through to clipboard fallback
        }
      }

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        // prevent scrolling to element
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }

      setBookingMessage('Link copied!');
      setTimeout(() => setBookingMessage(''), 1500);
    } catch (err) {
      // Expose the URL for manual copy when automatic copy fails
      setBookingMessage(`Unable to copy link. Press Ctrl+C to copy: ${shareUrl}`);
    }
  };

  const handleBook = async () => {
    if (!selectedSlot) {
      setBookingMessage('Please pick a slot.');
      return;
    }

    const slotCutoff = new Date(selectedSlot.end || selectedSlot.start).getTime();
    if (slotCutoff <= Date.now()) {
      setBookingMessage('That slot has already passed. Please choose another time.');
      setSelectedSlot(null);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setShowAuthForm(true);
      return;
    }

    try {
      setBookingState('loading');
      setBookingMessage('Locking in your time…');
      const res = await fetch(`${API_BASE_URL}/api/schedule/${slug}/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          slotStart: selectedSlot.start,
          slotEnd: selectedSlot.end,
          attendeeName: form.name,
          attendeeEmail: form.email,
          note: form.note
        })
      });

      const payload = await res.json();
      if (!res.ok) {
        throw new Error(payload.message || 'Booking failed');
      }

      setBookingState('success');
      setBookingMessage('Booked! Redirecting you to your dashboard…');
      setTimeout(() => { window.location.href = '/'; }, 2200);
    } catch (err) {
      setBookingState('error');
      setBookingMessage(err.message);
    }
  };

  const busyIntervals = useMemo(
    () => (selectedDay?.busy || []).map(block => [new Date(block.start).getTime(), new Date(block.end).getTime()]),
    [selectedDay]
  );

  const slotHasConflict = (slot) => {
    if (!busyIntervals.length) return false;
    const slotStart = new Date(slot.start).getTime();
    const slotEnd = new Date(slot.end).getTime();
    return busyIntervals.some(([busyStart, busyEnd]) => busyStart < slotEnd && busyEnd > slotStart);
  };

  const busyBlocks = useMemo(() => {
    if (!selectedDay) return [];
    const total = Math.max(workTotalMinutes, 1);
    return (selectedDay.busy || []).map(block => {
      const startMinutes = minutesFromMidnight(block.start);
      const endMinutes = minutesFromMidnight(block.end);
      const left = Math.max(((startMinutes - workStartMinutes) / total) * 100, 0);
      const width = Math.min(((endMinutes - startMinutes) / total) * 100, 100);
      const detailLabel = `${formatTime(block.start)} – ${formatTime(block.end)}`;
      const displayLabel = isOwner ? detailLabel : 'Booked';
      return { left, width, detailLabel, displayLabel, key: `${block.start}-${block.end}` };
    });
  }, [selectedDay, workStartMinutes, workTotalMinutes, hostTimeFormatter24, formatTime, isOwner]);

  useEffect(() => {
    if (!selectedSlot) return;
    if (slotHasConflict(selectedSlot)) {
      setSelectedSlot(null);
    }
  }, [selectedSlot, busyIntervals]);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-[#0F172A]' : 'bg-slate-50'}`}>
        <Loader2 className="animate-spin text-teal-500" size={36} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center text-center px-6 ${darkMode ? 'bg-[#0F172A] text-white' : 'bg-slate-50 text-slate-900'}`}>
        <Shield size={48} className="mb-4 text-red-500" />
        <h2 className="text-2xl font-bold mb-2">Scheduling link unavailable</h2>
        <p className="max-w-md text-sm opacity-80">{error}</p>
      </div>
    );
  }

  return (
    <div className={`${pageBg} min-h-screen font-sans`}>
      <div className="max-w-6xl mx-auto py-10 px-4 md:px-8">
        <header className={`flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-6 border-b ${headerBorder}`}>
          <div className="flex items-center gap-3">
            <img src="/hicapy.png" alt="HiCapy" className="w-10 h-10" onError={e => (e.target.style.display = 'none')} />
            <div>
              <p className="text-xs uppercase tracking-widest text-teal-500 font-bold">HiCapy Sessions</p>
              <h1 className={`text-2xl md:text-3xl font-black ${headingText}`}>{hostHeadline}</h1>
              <p className={`text-sm max-w-2xl ${subText}`}>{hostWelcome}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${darkMode ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-700'}`}>
                  <Clock size={12} />
                  {hostWorkingHours?.start} – {hostWorkingHours?.end} ({hostTimezone})
                </span>
                <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${darkMode ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-700'}`}>
                  <Globe size={12} />
                  You’re viewing in {viewerTimezone}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {WEEKDAY_LABELS.map((label, idx) => (
                  <span
                    key={label}
                    className={`px-2 py-1 rounded-full text-[11px] font-semibold border ${workingDaySet.has(idx)
                        ? darkMode
                          ? 'bg-teal-500/30 text-teal-100 border-teal-400/50'
                          : 'bg-teal-100 text-teal-700 border-teal-200'
                        : darkMode
                          ? 'border-white/10 text-white/30'
                          : 'border-slate-200 text-slate-400'
                      }`}
                  >
                    {label}
                  </span>
                ))}
                {!hostWorkingDays?.length && (
                  <span className={`text-xs ${subText}`}>No working days set yet.</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setDarkMode?.(!darkMode)}
              className={`p-2 rounded-full transition-colors ${darkMode ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button onClick={copyLink} className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold ${darkMode ? 'bg-white text-[#0F172A]' : 'bg-slate-900 text-white'}`}>
              <Copy size={14} /> Share Link
            </button>
          </div>
        </header>

        <div className="mt-8 grid lg:grid-cols-[1.2fr_0.8fr] gap-8">
          <section className={`${panelClass} rounded-2xl p-5`}>
            <div className="flex items-center gap-4 flex-wrap">
              <div className={`flex items-center gap-2 text-sm ${subText}`}>
                <Calendar size={16} />
                {meetingDurationMinutes}-minute meetings
              </div>
              <div className={`flex items-center gap-2 text-sm ${subText}`}>
                <Clock size={16} />
                Host timezone · {hostTimezone}
              </div>
              <div className={`flex items-center gap-2 text-sm ${subText}`}>
                <Users size={16} />
                Live product deep-dive + Q&A
              </div>
            </div>

            {hasAvailability ? (
              <div className="mt-6 flex gap-2 overflow-x-auto pb-2">
                {availability?.days?.map((day, idx) => (
                  <button
                    key={day.date}
                    onClick={() => {
                      setSelectedDayIndex(idx);
                      setSelectedSlot(null);
                    }}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold border transition ${idx === selectedDayIndex
                      ? 'bg-teal-500 text-white border-teal-400'
                      : darkMode
                        ? 'bg-transparent text-white border-white/20 hover:border-white/50'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                  >
                    {day.readable}
                    <span className="block text-[11px] font-normal opacity-80">{day.slots.length} slots</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className={`mt-6 p-4 rounded-2xl border text-sm ${darkMode ? 'border-white/10 text-slate-200 bg-white/5' : 'border-slate-200 text-slate-600 bg-slate-50'}`}>
                No working days fall within the visible window. Update your preferences or check back later.
              </div>
            )}

            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {selectedDay?.slots?.length ? (
                selectedDay.slots.map(slot => {
                  const slotCutoff = new Date(slot.end || slot.start).getTime();
                  const slotIsPast = slotCutoff <= nowMs;
                  const slotIsBusy = slotHasConflict(slot);
                  const isSelected = selectedSlot?.start === slot.start;
                  const baseClass = isSelected
                    ? 'bg-teal-500 text-white border-teal-400'
                    : darkMode
                      ? 'bg-white/5 text-white border-white/10 hover:border-white/40'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400';
                  const disabledClass = slotIsPast || slotIsBusy ? 'opacity-40 cursor-not-allowed hover:border-transparent' : '';
                  const hostRange = slot.label || formatHostRange(slot);
                  const viewerRange = formatViewerRange(slot);
                  return (
                    <button
                      key={slot.start}
                      onClick={() => setSelectedSlot(slot)}
                      disabled={slotIsPast || slotIsBusy}
                      className={`px-4 py-3 rounded-xl border text-left transition ${baseClass} ${disabledClass}`}
                    >
                      <p className="text-sm font-semibold">{hostRange}</p>
                      <p className={`text-[11px] ${darkMode ? 'text-white/70' : 'text-slate-500'}`}>Host time · {hostTimezone}</p>
                      <p className={`text-[11px] mt-1 ${darkMode ? 'text-white/70' : 'text-slate-500'}`}>
                        Your time ({viewerTimezone}): {viewerRange}
                      </p>
                      {slotIsPast && <p className="text-[10px] text-amber-400 mt-1">Past slot</p>}
                      {slotIsBusy && <p className="text-[10px] text-rose-400 mt-1">Already booked</p>}
                    </button>
                  );
                })
              ) : (
                <div className={`col-span-2 text-center text-sm py-8 ${subText}`}>No availability on this day.</div>
              )}
            </div>

            <div className="mt-8">
              <p className={`text-xs uppercase tracking-widest mb-2 ${labelText}`}>Calendar Snapshot</p>
              <div
                className={`relative w-full h-24 rounded-2xl overflow-hidden border ${darkMode ? 'border-white/10 bg-gradient-to-r from-white/5 via-white/0 to-white/10' : 'border-slate-200 bg-gradient-to-r from-emerald-50 via-white to-cyan-50'
                  }`}
              >
                <div className="absolute inset-0 flex">
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <div
                      key={`grid-${idx}`}
                      className={`flex-1 ${idx < 5 ? (darkMode ? 'border-r border-white/5' : 'border-r border-white/60') : ''}`}
                    />
                  ))}
                </div>
                <div className={`absolute inset-x-6 top-3 flex justify-between text-[11px] font-semibold tracking-widest ${darkMode ? 'text-white/80' : 'text-slate-500'}`}>
                  <span>{hostWorkingHours?.start}</span>
                  <span>{hostWorkingHours?.end}</span>
                </div>
                {busyBlocks?.map(block => {
                  const minWidth = Math.max(block.width, 4);
                  const safeWidth = Math.min(minWidth, Math.max(100 - block.left, 4));
                  return (
                    <div
                      key={block.key}
                      className="absolute top-5 bottom-5 bg-rose-500/60 border border-rose-500/80 rounded-2xl shadow-lg shadow-rose-900/20 flex items-center justify-center overflow-hidden"
                      style={{ left: `${block.left}%`, width: `${safeWidth}%` }}
                      title={isOwner ? block.detailLabel : 'Booked'}
                    >
                      <span className={`text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap px-2 ${busyLabelText}`}>
                        {block.displayLabel}
                      </span>
                    </div>
                  );
                })}
                <div className={`absolute inset-x-6 bottom-3 text-[11px] ${darkMode ? 'text-white/70' : 'text-slate-600'}`}>
                  Timeline in {hostTimezone}
                </div>
              </div>
              <div className={`mt-3 flex flex-wrap gap-4 text-[11px] ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                <span className="inline-flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-rose-500" /> Busy hold
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-teal-500" /> Working window {hostWorkingHours?.start} – {hostWorkingHours?.end}
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full border border-slate-400" /> Auto-translated to {viewerTimezone}
                </span>
              </div>
              <p className={`mt-2 text-[11px] ${subText}`}>
                {isOwner
                  ? 'Viewing as host — your private holds stay highlighted so you never double-book.'
                  : `Guests only see “Booked,” but every slot translates into ${viewerTimezone} automatically.`}
              </p>
              <div className={`mt-3 p-4 rounded-xl ${softPanelClass}`}>
                <p className={`text-xs uppercase tracking-widest font-semibold ${labelText}`}>Confirmed meetings</p>
                {busyBlocks.length ? (
                  <div className="mt-3 space-y-2">
                    {busyBlocks.map(block => (
                      <div key={`${block.key}-list`} className="flex items-center justify-between text-sm">
                        <span className={darkMode ? 'text-white' : 'text-slate-900'}>
                          {isOwner ? `${block.detailLabel} (${hostTimezone})` : 'Booked slot'}
                        </span>
                        <span className={`text-[11px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {isOwner ? 'Visible only to you' : 'Connect calendar to view details'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`mt-3 text-sm ${subText}`}>
                    {isOwner ? 'Nothing blocks this day yet.' : 'No booked holds yet.'}
                  </p>
                )}
              </div>
            </div>
          </section>

          <aside className={`${darkMode ? 'bg-white/5 border border-white/10' : 'bg-white border border-slate-100 shadow-xl'} rounded-2xl p-6 space-y-6`}>
            <div>
              <p className={`text-xs uppercase tracking-widest mb-1 ${labelText}`}>Hosted by</p>
              <h2 className={`text-xl font-bold ${headingText}`}>{hostName}</h2>
              <p className={`text-sm ${subText}`}>{hostTagline}</p>
            </div>

            <div className={`p-4 rounded-xl border ${selectedSlot
              ? darkMode ? 'border-teal-500/40 bg-teal-500/10' : 'border-teal-200 bg-teal-50'
              : darkMode ? 'border-white/10 bg-white/5' : 'border-slate-100 bg-slate-50'}`}>
              <p className={`text-xs font-semibold mb-1 ${labelText}`}>Selected slot</p>
              {selectedSlot ? (
                <div>
                  <p className={`text-lg font-bold ${headingText}`}>{formatHostRange(selectedSlot)}</p>
                  <p className={`text-xs ${subText}`}>Host time · {hostTimezone}</p>
                  <p className={`text-xs mt-1 ${subText}`}>
                    Your time ({viewerTimezone}): {formatViewerRange(selectedSlot)}
                  </p>
                  <p className={`text-[11px] mt-1 ${subText}`}>
                    {new Date(selectedSlot.start).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                  </p>
                </div>
              ) : (
                <p className={`text-sm ${subText}`}>Pick a time from the left.</p>
              )}
            </div>

            <div className="space-y-3">
              <label className={`text-xs font-bold ${labelText}`}>Your name</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className={`w-full px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-teal-500 ${inputThemeClass}`}
                placeholder="Jane Founder"
              />
              <label className={`text-xs font-bold ${labelText}`}>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className={`w-full px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-teal-500 ${inputThemeClass}`}
                placeholder="you@company.com"
              />
              <label className={`text-xs font-bold ${labelText}`}>Agenda / context (optional)</label>
              <textarea
                rows={3}
                value={form.note}
                onChange={e => setForm({ ...form, note: e.target.value })}
                className={`w-full px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-teal-500 ${inputThemeClass}`}
                placeholder="Anything you'd like us to prep ahead of time"
              />
            </div>

            <div className={`space-y-2 text-sm ${subText}`}>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-teal-500" /> Instant calendar invite</div>
              <div className="flex items-center gap-2"><Mail size={16} className="text-teal-500" /> Confirmation email from HiCapy</div>
              <div className="flex items-center gap-2"><MapPin size={16} className="text-teal-500" /> Google Meet link auto-generated</div>
            </div>

            {bookingMessage && (
              <div className={`text-sm rounded-lg px-3 py-2 ${bookingState === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-teal-50 text-teal-700 border border-teal-200'}`}>
                {bookingMessage}
              </div>
            )}

            <button
              onClick={handleBook}
              disabled={!selectedSlot || bookingState === 'loading'}
              className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white py-3 rounded-xl font-bold disabled:opacity-50"
            >
              {bookingState === 'loading' ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />}
              {bookingState === 'success' ? 'Booked' : 'Book this slot'}
            </button>

            <p className={`text-xs text-center ${subText}`}>You'll be redirected to the HiCapy dashboard after booking.</p>
          </aside>
        </div>
      </div>

      {showAuthForm && (
        <AuthForm
          onClose={() => setShowAuthForm(false)}
          onSuccess={(payload) => {
            setShowAuthForm(false);
            setViewer(payload.user);
            setForm(prev => ({
              ...prev,
              name: payload.user?.username || prev.name,
              email: payload.user?.email || prev.email
            }));
          }}
        />
      )}
    </div>
  );
};

export default SchedulingPage;
